import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type {
  HomeCarouselProductsData,
  HomeCarouselType,
  HomeCarouselVendorsData,
  HomeCarouselsData,
  PublicProductFilterQuery,
} from '@/common/types/home-carousel.type';
import type { ResponseItemType } from '@/common/types/response.type';
import Client from '@/common/utils/client.utility';
import { createQueryStringUtility } from '@/common/utils/create-query-string.utility';

export const HOME_CAROUSEL_TAKE = 20;

export type HomeCarouselsResponse = ResponseItemType<HomeCarouselsData>;
export type HomeCarouselProductsResponse =
  ResponseItemType<HomeCarouselProductsData>;
export type HomeCarouselVendorsResponse =
  ResponseItemType<HomeCarouselVendorsData>;

export interface HomeCarouselsQuery extends PublicProductFilterQuery {
  take?: number;
}

export interface HomeCarouselPagedQuery extends PublicProductFilterQuery {
  pageIndex?: number;
  pageSize?: number;
}

function toPublicFilterParams(query: PublicProductFilterQuery = {}) {
  return {
    ...(query.hasFreeShipping === true ? { HasFreeShipping: true } : {}),
    ...(query.hasDiscount === true ? { HasDiscount: true } : {}),
    ...(query.minPrice !== undefined ? { MinPrice: query.minPrice } : {}),
    ...(query.maxPrice !== undefined ? { MaxPrice: query.maxPrice } : {}),
    ...(query.sort ? { Sort: query.sort } : {}),
  };
}

export const GetHomeCarousels = async (query: HomeCarouselsQuery = {}) => {
  const client = new Client(true);
  return await client.get<HomeCarouselsResponse>(
    `/v1/Public/HomeCarousels${createQueryStringUtility({
      Take: query.take ?? HOME_CAROUSEL_TAKE,
      ...toPublicFilterParams(query),
    })}`
  );
};

export const useHomeCarousels = (query: HomeCarouselsQuery = {}) =>
  useQuery({
    queryKey: ['Get-Home-Carousels', query],
    queryFn: async () => await GetHomeCarousels(query),
    staleTime: 60_000,
  });

export const GetHomeCarouselByType = async (
  type: HomeCarouselType,
  query: HomeCarouselsQuery = {}
) => {
  const client = new Client(true);
  return await client.get<HomeCarouselsResponse>(
    `/v1/Public/HomeCarousels/${type}${createQueryStringUtility({
      Take: query.take ?? HOME_CAROUSEL_TAKE,
      ...toPublicFilterParams(query),
    })}`
  );
};

export const GetHomeCarouselProducts = async (
  type: HomeCarouselType,
  query: HomeCarouselPagedQuery = {}
) => {
  const client = new Client(true);
  return await client.get<HomeCarouselProductsResponse>(
    `/v1/Public/HomeCarousels/${type}/Products${createQueryStringUtility({
      PageIndex: query.pageIndex ?? 0,
      PageSize: query.pageSize ?? HOME_CAROUSEL_TAKE,
      ...toPublicFilterParams(query),
    })}`
  );
};

export const useHomeCarouselProducts = (
  type: HomeCarouselType | undefined,
  query: HomeCarouselPagedQuery = {}
) =>
  useQuery({
    queryKey: ['Get-Home-Carousel-Products', type, query],
    queryFn: async () => {
      if (!type) {
        throw new Error('carousel type is required');
      }
      return await GetHomeCarouselProducts(type, query);
    },
    enabled: Boolean(type),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });

export const GetHomeCarouselVendors = async (
  type: HomeCarouselType,
  query: HomeCarouselPagedQuery = {}
) => {
  const client = new Client(true);
  return await client.get<HomeCarouselVendorsResponse>(
    `/v1/Public/HomeCarousels/${type}/Vendors${createQueryStringUtility({
      PageIndex: query.pageIndex ?? 0,
      PageSize: query.pageSize ?? HOME_CAROUSEL_TAKE,
    })}`
  );
};

export const useHomeCarouselVendors = (
  type: HomeCarouselType | undefined,
  query: HomeCarouselPagedQuery = {}
) =>
  useQuery({
    queryKey: ['Get-Home-Carousel-Vendors', type, query],
    queryFn: async () => {
      if (!type) {
        throw new Error('carousel type is required');
      }
      return await GetHomeCarouselVendors(type, query);
    },
    enabled: Boolean(type),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
